/**
 * System Sequence Templates
 *
 * Pre-built email sequence templates for common use cases.
 * Users can copy these templates to create their own sequences.
 *
 * @see Issue #400 - System Templates for First-Time User Experience
 */

import type { SequenceTemplate } from './types';

// =============================================================================
// SEQUENCE TEMPLATE IDs
// =============================================================================

export const SEQUENCE_TEMPLATE_IDS = {
  NEW_LEAD_NURTURE: 'tpl-seq-new-lead-nurture',
  RE_ENGAGEMENT: 'tpl-seq-re-engagement',
  POST_CALL_FOLLOWUP: 'tpl-seq-post-call-followup',
  APPLICATION_REMINDER: 'tpl-seq-application-reminder',
  REFERRAL_REQUEST: 'tpl-seq-referral-request',
} as const;

// =============================================================================
// SEQUENCE TEMPLATES
// =============================================================================

export const SEQUENCE_TEMPLATES: SequenceTemplate[] = [
  // ---------------------------------------------------------------------------
  // New Lead Nurture Sequence
  // ---------------------------------------------------------------------------
  {
    id: SEQUENCE_TEMPLATE_IDS.NEW_LEAD_NURTURE,
    name: 'New Lead Nurture',
    description:
      'Welcome and educate new leads over 2 weeks with proven messaging that builds trust and drives applications.',
    category: 'lead-generation',
    icon: 'spark',
    color: '#4CAF50',
    tags: ['welcome', 'nurture', 'new-lead', 'education', 'onboarding'],
    estimatedDuration: '14 days',
    complexity: 'beginner',
    industryFit: ['real_estate', 'lending', 'mortgage'],
    data: {
      triggerType: 'event',
      triggerConfig: { event: 'lead.created' },
      goalType: 'conversion',
      goalConfig: { action: 'application.submitted' },
      sendOnWeekends: false,
      sendHoursStart: 9,
      sendHoursEnd: 17,
      steps: [
        {
          order: 1,
          name: 'Welcome Email',
          stepType: 'EMAIL',
          delayDays: 0,
          delayHours: 0,
          emailSubject: "Welcome to {{company}} - Let's Get You Started",
          emailBody: `<p>Hi {{first_name}},</p>

<p>Thank you for your interest in {{company}}. I'm excited to help you find the right financing solution for your investment goals.</p>

<p>Quick question: <strong>What type of property are you looking to finance?</strong></p>

<ul>
  <li>Fix & Flip</li>
  <li>Rental Property (DSCR)</li>
  <li>Bridge Loan</li>
  <li>New Construction</li>
</ul>

<p>Just reply to this email and let me know - I'll send you some relevant information to help you get started.</p>

<p>Looking forward to connecting,<br/>
{{sender_name}}<br/>
{{sender_title}}</p>`,
          emailText:
            'Hi {{first_name}}, Thank you for your interest in {{company}}. What type of property are you looking to finance? Reply to let me know!',
        },
        {
          order: 2,
          name: 'Value Proposition',
          stepType: 'EMAIL',
          delayDays: 2,
          delayHours: 0,
          emailSubject: 'Why investors choose {{company}}',
          emailBody: `<p>Hi {{first_name}},</p>

<p>I wanted to share what sets us apart from other lenders:</p>

<ul>
  <li><strong>Fast Closings</strong> - Close in as fast as 7-10 days</li>
  <li><strong>Competitive Rates</strong> - Rates starting at 9.99%</li>
  <li><strong>Flexible Terms</strong> - 12-24 month terms available</li>
  <li><strong>Dedicated Support</strong> - Your own loan officer from start to finish</li>
</ul>

<p>We've helped hundreds of investors like you fund their projects. Whether you're doing your first flip or your fiftieth, we have programs designed for your experience level.</p>

<p>Ready to discuss your project? <a href="{{calendar_link}}">Book a quick call</a> and let's talk numbers.</p>

<p>Best,<br/>
{{sender_name}}</p>`,
          emailText:
            'Hi {{first_name}}, Here is what sets us apart: Fast closings (7-10 days), competitive rates (starting at 9.99%), flexible terms, and dedicated support. Ready to discuss? Book a call!',
        },
        {
          order: 3,
          name: 'Success Story',
          stepType: 'EMAIL',
          delayDays: 4,
          delayHours: 0,
          emailSubject: 'How {{success_name}} funded 15 deals in 12 months',
          emailBody: `<p>Hi {{first_name}},</p>

<p>I wanted to share a quick success story that might resonate with you.</p>

<p>{{success_name}} came to us last year with a single property and a big vision. Fast forward 12 months, and they've successfully completed <strong>15 fix-and-flip projects</strong> using our financing.</p>

<p><strong>The key to their success?</strong></p>

<ol>
  <li>Starting with a clear investment strategy</li>
  <li>Building a reliable contractor network</li>
  <li>Having a financing partner who could move fast</li>
</ol>

<p>That last point is where we came in. When {{success_name}} found a great deal, we were able to fund it in just 8 days - before the competition even got their paperwork together.</p>

<p>I'd love to help you write your own success story. What's holding you back from getting started?</p>

<p>{{sender_name}}</p>`,
          emailText:
            'Hi {{first_name}}, Quick success story: {{success_name}} funded 15 deals in 12 months with our help. The key? Fast financing when opportunities arise. What is holding you back?',
        },
        {
          order: 4,
          name: 'Call to Action',
          stepType: 'EMAIL',
          delayDays: 7,
          delayHours: 0,
          emailSubject: "Ready to get started? Here's your next step",
          emailBody: `<p>Hi {{first_name}},</p>

<p>I've shared a lot about what we do and how we help investors succeed. Now I'm curious - <strong>where are you in your investment journey?</strong></p>

<p>If you're ready to move forward, here's what I recommend:</p>

<p><strong>Option 1: Apply Online (5 minutes)</strong><br/>
Get pre-qualified in minutes. No hard credit pull, no obligation.<br/>
<a href="{{apply_link}}">[Start Your Application]</a></p>

<p><strong>Option 2: Schedule a Call</strong><br/>
Have questions? Let's talk through your specific situation.<br/>
<a href="{{calendar_link}}">[Book a 15-Minute Call]</a></p>

<p>Either way, I'm here to help when you're ready.</p>

<p>{{sender_name}}<br/>
{{sender_phone}}</p>`,
          emailText:
            'Hi {{first_name}}, Ready to move forward? Option 1: Apply online (5 min, no hard credit pull). Option 2: Schedule a call to discuss your situation. I am here when you are ready!',
        },
      ],
    },
  },

  // ---------------------------------------------------------------------------
  // Re-engagement Campaign
  // ---------------------------------------------------------------------------
  {
    id: SEQUENCE_TEMPLATE_IDS.RE_ENGAGEMENT,
    name: 'Re-engagement Campaign',
    description:
      'Win back cold leads who went quiet. A 3-email sequence designed to reignite interest without being pushy.',
    category: 're-engagement',
    icon: 'refresh',
    color: '#FF9800',
    tags: ['re-engagement', 'win-back', 'cold-leads', 'follow-up'],
    estimatedDuration: '10 days',
    complexity: 'beginner',
    industryFit: ['real_estate', 'lending', 'mortgage', 'general'],
    data: {
      triggerType: 'segment',
      triggerConfig: { segment: 'inactive_leads', inactiveDays: 30 },
      goalType: 'reply',
      goalConfig: { action: 'email.replied' },
      sendOnWeekends: false,
      sendHoursStart: 10,
      sendHoursEnd: 16,
      steps: [
        {
          order: 1,
          name: 'Check-In',
          stepType: 'EMAIL',
          delayDays: 0,
          delayHours: 0,
          emailSubject: 'Quick check-in, {{first_name}}',
          emailBody: `<p>Hi {{first_name}},</p>

<p>It's been a while since we last connected, and I wanted to check in.</p>

<p>Are you still looking to finance an investment property? If so, I'd love to help.</p>

<p>If your plans have changed, no worries at all - just let me know and I'll update my notes.</p>

<p>Either way, I hope you're doing well!</p>

<p>{{sender_name}}</p>`,
          emailText:
            'Hi {{first_name}}, It has been a while! Are you still looking to finance an investment property? Let me know either way.',
        },
        {
          order: 2,
          name: "What's New",
          stepType: 'EMAIL',
          delayDays: 4,
          delayHours: 0,
          emailSubject: 'A few things have changed since we last spoke',
          emailBody: `<p>Hi {{first_name}},</p>

<p>Since we last connected, we've made some updates I thought you'd want to know about:</p>

<ul>
  <li><strong>Lower rates</strong> - Our rates have come down, now starting at {{current_rate}}%</li>
  <li><strong>Faster closings</strong> - We've streamlined our process to close even faster</li>
  <li><strong>New programs</strong> - We've added DSCR loans and ground-up construction</li>
</ul>

<p>If any of these updates make a difference for your investment plans, I'd love to reconnect.</p>

<p>What does your pipeline look like these days?</p>

<p>{{sender_name}}</p>`,
          emailText:
            'Hi {{first_name}}, Since we last spoke: lower rates (starting at {{current_rate}}%), faster closings, and new programs. What does your pipeline look like?',
        },
        {
          order: 3,
          name: 'Final Touch',
          stepType: 'EMAIL',
          delayDays: 6,
          delayHours: 0,
          emailSubject: 'Should I close your file?',
          emailBody: `<p>Hi {{first_name}},</p>

<p>I haven't heard back from you, so I wanted to reach out one more time before I close your file.</p>

<p>If you're no longer interested in investment property financing, that's completely fine - just let me know and I'll stop reaching out.</p>

<p>But if you're still considering it and just haven't had time to respond, reply with "yes" and I'll keep your file open for when you're ready.</p>

<p>Either way, I wish you all the best!</p>

<p>{{sender_name}}</p>`,
          emailText:
            'Hi {{first_name}}, Should I close your file? If you are still interested, just reply "yes" and I will keep it open. Either way, wishing you the best!',
        },
      ],
    },
  },

  // ---------------------------------------------------------------------------
  // Post-Call Follow-up
  // ---------------------------------------------------------------------------
  {
    id: SEQUENCE_TEMPLATE_IDS.POST_CALL_FOLLOWUP,
    name: 'Post-Call Follow-up',
    description:
      'Follow up after phone calls or meetings. Keep the momentum going with helpful next steps and resources.',
    category: 'follow-up',
    icon: 'phone_callback',
    color: '#2196F3',
    tags: ['follow-up', 'post-call', 'meeting', 'consultation'],
    estimatedDuration: '7 days',
    complexity: 'beginner',
    industryFit: ['real_estate', 'lending', 'mortgage', 'general'],
    data: {
      triggerType: 'manual',
      goalType: 'conversion',
      goalConfig: { action: 'application.submitted' },
      sendOnWeekends: false,
      sendHoursStart: 9,
      sendHoursEnd: 17,
      steps: [
        {
          order: 1,
          name: 'Thank You + Summary',
          stepType: 'EMAIL',
          delayDays: 0,
          delayHours: 2,
          emailSubject: 'Great speaking with you, {{first_name}}!',
          emailBody: `<p>Hi {{first_name}},</p>

<p>Thank you for taking the time to speak with me today. I really enjoyed learning about your investment goals!</p>

<p><strong>Here's a quick summary of what we discussed:</strong></p>

<ul>
  <li>Property type: {{property_type}}</li>
  <li>Estimated purchase price: {{purchase_price}}</li>
  <li>Target closing date: {{target_date}}</li>
</ul>

<p><strong>Next steps:</strong></p>
<ol>
  <li>{{next_step_1}}</li>
  <li>{{next_step_2}}</li>
  <li>{{next_step_3}}</li>
</ol>

<p>I'll follow up in a few days to see how things are progressing. In the meantime, feel free to reach out if you have any questions!</p>

<p>{{sender_name}}<br/>
{{sender_phone}}</p>`,
          emailText:
            'Hi {{first_name}}, Great speaking with you! Here is a summary of what we discussed and the next steps. I will follow up in a few days. Questions? Just reply!',
        },
        {
          order: 2,
          name: 'Resource Share',
          stepType: 'EMAIL',
          delayDays: 2,
          delayHours: 0,
          emailSubject: 'Resources for your {{property_type}} project',
          emailBody: `<p>Hi {{first_name}},</p>

<p>I put together some resources that should help with your {{property_type}} project:</p>

<ul>
  <li><a href="{{resource_link_1}}">{{resource_title_1}}</a></li>
  <li><a href="{{resource_link_2}}">{{resource_title_2}}</a></li>
  <li><a href="{{resource_link_3}}">{{resource_title_3}}</a></li>
</ul>

<p>Have you had a chance to {{next_step_1}} yet? Let me know if you need any help!</p>

<p>{{sender_name}}</p>`,
          emailText:
            'Hi {{first_name}}, Here are some resources for your project. Have you had a chance to work on the next steps yet? Let me know if you need help!',
        },
        {
          order: 3,
          name: 'Progress Check',
          stepType: 'EMAIL',
          delayDays: 5,
          delayHours: 0,
          emailSubject: "How's the {{property_type}} project coming along?",
          emailBody: `<p>Hi {{first_name}},</p>

<p>Just checking in on your {{property_type}} project. How are things progressing?</p>

<p>If you're ready to move forward with financing, I can have a pre-qualification letter ready for you within 24 hours.</p>

<p>Or if you've hit any roadblocks, let me know - I might be able to help or point you in the right direction.</p>

<p>What's your status?</p>

<p>{{sender_name}}</p>`,
          emailText:
            'Hi {{first_name}}, How is your project coming along? If ready, I can have a pre-qual letter in 24 hours. Hit any roadblocks? Let me know!',
        },
      ],
    },
  },

  // ---------------------------------------------------------------------------
  // Application Reminder
  // ---------------------------------------------------------------------------
  {
    id: SEQUENCE_TEMPLATE_IDS.APPLICATION_REMINDER,
    name: 'Application Reminder',
    description: "Remind borrowers who started but didn't complete their application. Offer help and create urgency.",
    category: 'follow-up',
    icon: 'assignment_late',
    color: '#F44336',
    tags: ['reminder', 'application', 'incomplete', 'stalled', 'conversion'],
    estimatedDuration: '5 days',
    complexity: 'beginner',
    industryFit: ['lending', 'mortgage', 'fintech'],
    data: {
      triggerType: 'segment',
      triggerConfig: { segment: 'incomplete_applications', stalledDays: 2 },
      goalType: 'conversion',
      goalConfig: { action: 'application.completed' },
      sendOnWeekends: false,
      sendHoursStart: 10,
      sendHoursEnd: 16,
      steps: [
        {
          order: 1,
          name: 'Friendly Reminder',
          stepType: 'EMAIL',
          delayDays: 0,
          delayHours: 0,
          emailSubject: 'Your application is waiting, {{first_name}}',
          emailBody: `<p>Hi {{first_name}},</p>

<p>I noticed you started your loan application but didn't finish it. No worries - it happens!</p>

<p>Your progress has been saved, so you can <a href="{{resume_link}}">pick up right where you left off</a>.</p>

<p>The whole thing takes about 5 minutes to complete. Once submitted, I can review it and get back to you with pre-qualification within 24 hours.</p>

<p>Is there anything holding you back? Just reply and let me know - I'm happy to help!</p>

<p>{{sender_name}}</p>`,
          emailText:
            'Hi {{first_name}}, Your loan application is waiting! Your progress is saved - pick up where you left off. Takes about 5 minutes. Anything holding you back?',
        },
        {
          order: 2,
          name: 'Offer Help',
          stepType: 'EMAIL',
          delayDays: 2,
          delayHours: 0,
          emailSubject: 'Need help with your application?',
          emailBody: `<p>Hi {{first_name}},</p>

<p>I know applications can be tedious. If you're stuck on any part, I'd be happy to walk you through it.</p>

<p><strong>Common questions I can help with:</strong></p>
<ul>
  <li>"What documents do I need?"</li>
  <li>"What if I don't have all the property details yet?"</li>
  <li>"How do I calculate my renovation budget?"</li>
</ul>

<p>Want me to call you and help you finish it over the phone? Just reply with a good time and your number.</p>

<p>{{sender_name}}<br/>
{{sender_phone}}</p>`,
          emailText:
            'Hi {{first_name}}, Stuck on the application? I can help! Want me to call and walk you through it? Reply with a good time.',
        },
        {
          order: 3,
          name: 'Last Chance',
          stepType: 'EMAIL',
          delayDays: 3,
          delayHours: 0,
          emailSubject: 'Last chance: Your application expires soon',
          emailBody: `<p>Hi {{first_name}},</p>

<p>Just a heads up - your incomplete application will expire in <strong>7 days</strong>.</p>

<p>If you're still interested in financing your investment property, I'd recommend finishing it before then so you don't have to start over.</p>

<p><a href="{{resume_link}}">[Complete My Application]</a></p>

<p>If your plans have changed, no worries - just let me know and I'll remove you from these reminders.</p>

<p>{{sender_name}}</p>`,
          emailText:
            "Hi {{first_name}}, Your application expires in 7 days. Finish it now so you don't have to start over. Plans changed? Let me know!",
        },
      ],
    },
  },

  // ---------------------------------------------------------------------------
  // Referral Request
  // ---------------------------------------------------------------------------
  {
    id: SEQUENCE_TEMPLATE_IDS.REFERRAL_REQUEST,
    name: 'Referral Request',
    description: 'Ask happy customers for referrals. A gentle 3-touch sequence that makes it easy to recommend you.',
    category: 'referral',
    icon: 'group_add',
    color: '#9C27B0',
    tags: ['referral', 'word-of-mouth', 'customer', 'testimonial'],
    estimatedDuration: '21 days',
    complexity: 'beginner',
    industryFit: ['real_estate', 'lending', 'mortgage', 'general'],
    data: {
      triggerType: 'event',
      triggerConfig: { event: 'loan.funded', delayDays: 30 },
      goalType: 'engagement',
      goalConfig: { action: 'referral.submitted' },
      sendOnWeekends: true,
      sendHoursStart: 8,
      sendHoursEnd: 20,
      steps: [
        {
          order: 1,
          name: 'Soft Ask',
          stepType: 'EMAIL',
          delayDays: 0,
          delayHours: 0,
          emailSubject: 'Know anyone who could use our help?',
          emailBody: `<p>Hi {{first_name}},</p>

<p>I hope your investment is going well! It's been about a month since we closed your loan, and I wanted to check in.</p>

<p>If you know any fellow real estate investors who are looking for financing, I'd love an introduction. We offer a <strong>$500 referral bonus</strong> for any closed loan that comes from your recommendation.</p>

<p>No pressure at all - just thought I'd mention it in case anyone comes to mind.</p>

<p>How's the project going?</p>

<p>{{sender_name}}</p>`,
          emailText:
            "Hi {{first_name}}, Hope your investment is going well! Know any investors who need financing? We offer a $500 referral bonus. No pressure - just thought I'd mention it!",
        },
        {
          order: 2,
          name: 'Make It Easy',
          stepType: 'EMAIL',
          delayDays: 10,
          delayHours: 0,
          emailSubject: 'The easiest way to refer someone',
          emailBody: `<p>Hi {{first_name}},</p>

<p>I wanted to make it super easy for you to refer anyone who might benefit from our services.</p>

<p><strong>Here's your personal referral link:</strong><br/>
<a href="{{referral_link}}">{{referral_link}}</a></p>

<p>Anyone who applies through this link will automatically be connected to me, and you'll get $500 when their loan closes.</p>

<p>Feel free to share it with your investor network, real estate agent contacts, or anyone you think could benefit.</p>

<p>Thanks for being a great client!</p>

<p>{{sender_name}}</p>`,
          emailText:
            'Hi {{first_name}}, Here is your personal referral link: {{referral_link}}. Share it with your network and get $500 for each closed loan!',
        },
        {
          order: 3,
          name: 'Testimonial Request',
          stepType: 'EMAIL',
          delayDays: 11,
          delayHours: 0,
          emailSubject: 'Quick favor? (30 seconds)',
          emailBody: `<p>Hi {{first_name}},</p>

<p>I have a quick favor to ask that would mean a lot to me.</p>

<p>Would you be willing to leave a brief review of your experience working with us? It helps other investors like yourself feel confident choosing us.</p>

<p><a href="{{review_link}}">[Leave a Review]</a> (takes about 30 seconds)</p>

<p>Even just a sentence or two makes a huge difference. Thank you so much!</p>

<p>{{sender_name}}</p>`,
          emailText:
            'Hi {{first_name}}, Quick favor? Would you leave a brief review? Takes 30 seconds and helps other investors feel confident. Thank you!',
        },
      ],
    },
  },
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get all sequence templates
 */
export function getAllSequenceTemplates(): SequenceTemplate[] {
  return SEQUENCE_TEMPLATES;
}

/**
 * Get sequence template by ID
 */
export function getSequenceTemplateById(id: string): SequenceTemplate | undefined {
  return SEQUENCE_TEMPLATES.find((t) => t.id === id);
}

/**
 * Get sequence templates by category
 */
export function getSequenceTemplatesByCategory(category: string): SequenceTemplate[] {
  return SEQUENCE_TEMPLATES.filter((t) => t.category === category);
}

/**
 * Get sequence templates by complexity
 */
export function getSequenceTemplatesByComplexity(
  complexity: 'beginner' | 'intermediate' | 'advanced',
): SequenceTemplate[] {
  return SEQUENCE_TEMPLATES.filter((t) => t.complexity === complexity);
}

/**
 * Search sequence templates by name, description, or tags
 */
export function searchSequenceTemplates(query: string): SequenceTemplate[] {
  const lowerQuery = query.toLowerCase();
  return SEQUENCE_TEMPLATES.filter(
    (t) =>
      t.name.toLowerCase().includes(lowerQuery) ||
      t.description.toLowerCase().includes(lowerQuery) ||
      t.tags.some((tag) => tag.toLowerCase().includes(lowerQuery)),
  );
}
